import { useState } from "react";
import { submitWorkerJsonForm } from "../../common/formApi";
import PhoneInputField, {
  formatPhoneForSubmit,
  isCompletePhoneNumber,
} from "../../common/PhoneInputField";
import {
  applyEmailInputValidity,
  EMAIL_INPUT_PATTERN,
  isValidEmail,
} from "../../common/formUtils";

type ClassBookingFormProps = {
  classDate: string;
  preferredDate: string;
  sessionTime: string;
  partySize: number;
  isRequest: boolean;
  isFull: boolean;
  onCancel: () => void;
};

function requestTypeLabel(isFull: boolean, isRequest: boolean) {
  if (isFull) {
    return "Waitlist";
  }
  if (isRequest) {
    return "Date request";
  }
  return "Booking";
}

function ClassBookingForm({
  classDate,
  preferredDate,
  sessionTime,
  partySize,
  isRequest,
  isFull,
  onCancel,
}: ClassBookingFormProps) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    classType: "",
    decoratingClass: "",
    comments: "",
    website: "",
  });
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneResetKey, setPhoneResetKey] = useState(0);

  function handleChange(
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) {
    const { name, value } = e.target;
    if (e.target instanceof HTMLInputElement && e.target.type === "email") {
      applyEmailInputValidity(e.target);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formEl = e.currentTarget;

    if (formData.website.trim() !== "") {
      setSubmitError("Unable to submit this form. Please try again.");
      return;
    }

    if (!isValidEmail(formData.email)) {
      setSubmitError(
        "Please enter a valid email address (e.g. name@example.com).",
      );
      return;
    }

    if (!isCompletePhoneNumber(formData.phone)) {
      setSubmitError("Please enter a valid phone number.");
      return;
    }

    if (!preferredDate) {
      setSubmitError("Please choose a class date from the calendar.");
      return;
    }

    if (isRequest && (!formData.classType || !formData.decoratingClass)) {
      setSubmitError("Please choose a class type and decorating class.");
      return;
    }

    setSubmitError("");
    setSubmitSuccess("");
    setIsSubmitting(true);

    const requestLabel = requestTypeLabel(isFull, isRequest);
    const fullName = `${formData.firstName} ${formData.lastName}`.trim();

    try {
      await submitWorkerJsonForm("class-booking", {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formatPhoneForSubmit(formData.phone),
        requestType: requestLabel,
        classDate,
        classTime: sessionTime,
        partySize: isFull ? "" : String(partySize),
        classType: isRequest ? formData.classType : "",
        decoratingClass: isRequest ? formData.decoratingClass : "",
        comments: formData.comments,
        website: formData.website,
        subject: `Class ${requestLabel} from ${fullName}`,
      });

      setSubmitSuccess(
        isFull
          ? "Thank you! Your waitlist request was submitted successfully."
          : isRequest
            ? "Thank you! Your class date request was submitted successfully."
            : "Thank you! Your class booking was submitted successfully.",
      );
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        classType: "",
        decoratingClass: "",
        comments: "",
        website: "",
      });
      setPhoneResetKey((key) => key + 1);
      formEl.reset();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Could not submit right now. Please try again in a few minutes.";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form
      className="booking-form booking-container__form"
      onSubmit={handleSubmit}
    >
      <div>
        <label htmlFor="booking-first-name">
          First Name<span className="required">*</span>
        </label>
        <input
          type="text"
          id="booking-first-name"
          name="firstName"
          onChange={handleChange}
          required
        />
      </div>
      <div>
        <label htmlFor="booking-last-name">
          Last Name<span className="required">*</span>
        </label>
        <input
          type="text"
          id="booking-last-name"
          name="lastName"
          onChange={handleChange}
          required
        />
      </div>
      <div>
        <label htmlFor="booking-email">
          Email<span className="required">*</span>
        </label>
        <input
          type="email"
          id="booking-email"
          name="email"
          autoComplete="email"
          pattern={EMAIL_INPUT_PATTERN}
          onChange={handleChange}
          onInvalid={(e) => applyEmailInputValidity(e.currentTarget)}
          required
        />
      </div>
      <div>
        <label htmlFor="booking-phone">
          Phone<span className="required">*</span>
        </label>
        <PhoneInputField
          id="booking-phone"
          name="phone"
          value={formData.phone}
          onChange={(phone) => setFormData((prev) => ({ ...prev, phone }))}
          reset={phoneResetKey}
        />
      </div>
      {isRequest && (
        <>
          <div>
            <label htmlFor="booking-class-type">
              Class Type<span className="required">*</span>
            </label>
            <select
              id="booking-class-type"
              name="classType"
              value={formData.classType}
              onChange={handleChange}
              required
            >
              <option value="">Select one</option>
              <option value="Group">Group</option>
              <option value="Private">Private</option>
            </select>
          </div>
          <div>
            <label htmlFor="booking-decorating-class">
              Decorating Class<span className="required">*</span>
            </label>
            <select
              id="booking-decorating-class"
              name="decoratingClass"
              value={formData.decoratingClass}
              onChange={handleChange}
              required
            >
              <option value="">Select one</option>
              <option value="Cake">Cake</option>
              <option value="Cupcake">Cupcake</option>
              <option value="Cookie">Cookie</option>
              <option value="Fondant">Fondant</option>
              <option value="Flexible">Flexible</option>
            </select>
          </div>
        </>
      )}
      <div className="booking-form__comments">
        <label htmlFor="booking-comments">Comments (optional)</label>
        <textarea
          id="booking-comments"
          name="comments"
          rows={8}
          value={formData.comments}
          onChange={handleChange}
        />
      </div>
      <div className="honeypot-field" aria-hidden="true">
        <label htmlFor="booking-website">Website</label>
        <input
          type="text"
          id="booking-website"
          name="website"
          value={formData.website}
          onChange={handleChange}
          autoComplete="off"
          tabIndex={-1}
        />
      </div>
      <div className="booking-form__actions">
        <button className="btn" type="submit" disabled={isSubmitting}>
          {isSubmitting
            ? "Submitting..."
            : isFull
              ? "Add to waitlist"
              : isRequest
                ? "Request now"
                : "Book now"}
        </button>
        <button
          type="button"
          className="btn--cancel"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>
      {(submitError || submitSuccess) && (
        <p className="form-status">{submitError || submitSuccess}</p>
      )}
    </form>
  );
}

export default ClassBookingForm;
